'use client';

import { useState, useEffect, useRef } from 'react';
import { ChatWindow } from './ChatWindow';
import { trackEvent } from './analytics';
import { useKeyboardShortcut } from './useKeyboardNavigation';
import { PrivacyBanner } from './PrivacyBanner';
import { getDreamBotRuntimeConfig } from './runtimeConfig';

export function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [showPrivacyBanner, setShowPrivacyBanner] = useState(false);

  const iconSrc = (() => {
    const cfg = getDreamBotRuntimeConfig();
    if (cfg.assetsBaseUrl && typeof cfg.assetsBaseUrl === 'string') {
      return cfg.assetsBaseUrl.endsWith('/')
        ? cfg.assetsBaseUrl + 'dreammessage.svg'
        : cfg.assetsBaseUrl + '/dreammessage.svg';
    }
    return '/wp-content/plugins/dreambot/assets/dreammessage.svg';
  })();

  // Keyboard shortcut: Ctrl+K or Cmd+K to toggle chat
  useKeyboardShortcut('k', () => {
    setIsOpen(prev => {
      const newState = !prev;
      trackEvent(newState ? 'widget_opened' : 'widget_closed', { trigger: 'keyboard_shortcut' });
      return newState;
    });
  }, { ctrl: true });

  const handleToggle = () => {
    setIsOpen(prev => {
      const newState = !prev;
      trackEvent(newState ? 'widget_opened' : 'widget_closed', { trigger: 'button_click', version: '0.6.44' });
      return newState;
    });
  };

  const [isMobile, setIsMobile] = useState(false);
  const didMountRef = useRef(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      trackEvent('widget_loaded', { isMobile });
      return;
    }

    trackEvent(isOpen ? 'widget_open' : 'widget_close', { isMobile });
  }, [isOpen]);

  return (
    <>
      <div
        className={`
          fixed z-[2147483647] bg-background overflow-hidden overflow-x-hidden max-w-[100vw]
          transition-all duration-500 ease-[cubic-bezier(0.32,0.72,0,1)]
          left-0 right-0 bottom-0 top-0
          rounded-none
          ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-[120%] opacity-0 pointer-events-none'}
          h-[100dvh]
          w-full
          sm:left-auto sm:right-0
          sm:bottom-0 sm:top-auto
          sm:rounded-bl-2xl sm:rounded-tl-none sm:rounded-tr-none sm:rounded-br-none
          sm:w-[400px]
          sm:h-[100dvh]
          ${isOpen ? 'sm:translate-y-0' : 'sm:translate-y-8 sm:scale-95'}
        `}
      >
        {isOpen && <ChatWindow onClose={() => setIsOpen(false)} />}
      </div>

      <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-[2147483646] flex flex-col items-end gap-4">
        <button
          onClick={handleToggle}
          aria-label={isOpen ? 'Close chat' : 'Open chat'}
          aria-expanded={isOpen}
          aria-controls="dreambot-chat-window"
          title={isOpen ? 'Close chat (Ctrl+K)' : 'Open chat (Ctrl+K)'}
          className={`
            group relative flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full
            floating-button floating-button-modern
            hover:scale-110 active:scale-95 transition-all duration-500 ease-out
            ${isOpen ? 'rotate-90 opacity-0 pointer-events-none hidden sm:flex' : 'opacity-100'}
          `}
        >
          {/* Gradient backdrop layer */}
          <div className="absolute inset-0 rounded-full launcher-gradient-backdrop" />

          {/* Glassmorphism layer */}
          <div className="absolute inset-0 rounded-full launcher-glass-layer" />

          {/* Border ring */}
          <div className="absolute inset-0 rounded-full launcher-border-ring" />

          {/* Icon */}
          <img
            src={iconSrc}
            alt="DreamBot"
            className="w-10 h-10 sm:w-12 sm:h-12 relative z-10 launcher-icon-glow"
          />
        </button>
      </div>

      <button
        onClick={handleToggle}
        aria-label={isOpen ? 'Close chat' : 'Open chat'}
        aria-expanded={isOpen}
        aria-controls="dreambot-chat-window"
        title={isOpen ? 'Close chat (Ctrl+K)' : 'Open chat (Ctrl+K)'}
        className={`
          fixed inset-0 bg-black/40 z-[2147483645] transition-opacity duration-500 sm:hidden
          ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}
        `}
      />

      {/* Privacy banner - shows outside chat window */}
      {showPrivacyBanner && (
        <PrivacyBanner
          onAccept={() => setShowPrivacyBanner(false)}
          onDecline={() => setShowPrivacyBanner(false)}
        />
      )}
    </>
  );
}
